import { useSearchParams } from 'next/navigation';

import type { GTMEventGlycerinSort, GenericTrackingEvent } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { UseSortingFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseSortingFragmentFragmentDoc';
import { searchParamsToObject, usePushQueryChange } from '@packages/shared';

import { decodeOrder, encodeOrder } from '../queryEncoding';
import { getGlycerinSortMethod } from '../utils/tracking/getGlycerinSortMethod';

/* GraphQL */ `
  fragment UseSortingFragment on SearchProductResult {
    __typename
    defaultSortingKey
  }
`;

/**
 * Use sorting option persisted in the url query
 *
 * Changing sorting results in a router.push, which will trigger a data reload
 */
export const useSorting = (maskedData: FragmentType<typeof UseSortingFragmentFragmentDoc>) => {
  const { __typename: pageType, defaultSortingKey } = getFragmentData(
    UseSortingFragmentFragmentDoc,
    maskedData,
  );

  const dispatchGtmEvent = useTracking();
  const searchParams = useSearchParams();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const query = searchParamsToObject(searchParams);
  const sortingKey = decodeOrder(query) ?? defaultSortingKey;

  const pushSortingChange = (newOrder: string) => {
    if (newOrder === sortingKey) return;

    // default sorting key should not be visible in the URL
    const cleanSortingKey = newOrder === defaultSortingKey ? undefined : newOrder;

    pushQueryChange(encodeOrder(cleanSortingKey));

    dispatchGtmEvent<GTMEventGlycerinSort>({
      event: 'Sort',
      SortData: {
        sortMethod: getGlycerinSortMethod(newOrder, pageType),
      },
    });

    dispatchGtmEvent<GenericTrackingEvent>({
      event: 'ga-event',
      eventAction: 'Filter / Sort benutzt',
      eventCategory: 'User Interactions',
      eventLabel: 'sortiert',
    });
  };

  return {
    sortingKey,
    pushSortingChange,
  };
};
